import { Component, OnInit, AfterViewInit, ViewChild, ElementRef, Renderer2, NgZone, OnDestroy } from '@angular/core';
import { ComponentBase } from 'src/app/main/dynamic/component-base';
import { DynamicEvent } from 'src/app/main/dynamic/dynamic.event';
import { environment } from 'src/environments/environment';
import { SharedDataService } from 'src/app/service/shared-data.service';

declare var google: any;

@Component({
  selector: 'app-input-text-google-maps',
  templateUrl: './input-text-google-maps.component.html',
  styleUrls: ['./input-text-google-maps.component.scss']
})
export class InputTextGoogleMapsComponent extends ComponentBase implements OnInit, AfterViewInit, OnDestroy {

  @ViewChild('googleMapsInput', { static: false }) googleMapsInput!: ElementRef;
  @ViewChild('autocompleteList', { static: false }) autocompleteList!: ElementRef;

  autocompleteService: any;
  placesService: any;
  predictions: any[] = [];
  showPredictions = false;
  isLoading = false;
  searchText = '';
  private isApiLoaded = false;
  private isServicesInitialized = false;
  private initRetryCount = 0;
  private readonly maxRetries = 5;
  private initialValue: string;

  constructor(
    private renderer: Renderer2,
    private ngZone: NgZone,
    private sharedDataService: SharedDataService
  ) {
    super();
  }

  private get valueChanged(): boolean {
    return this.initialValue != this.attribute.value;
  }

  ngOnInit() {
    this.initialValue = this.attribute.value;
    this.shareData();
    this.loadGoogleMapsAPI();
  }

  ngAfterViewInit() {
    console.log('Google Maps component initialized');
    this.initializeInputValue();
  }

  ngOnDestroy() {
    if(this.attribute.uid) {
      this.sharedDataService.remove(this.attribute.uid);
    }
  }

  private initializeInputValue(): void {
    // Se já há um valor definido no attribute, exibir no campo de input
    if (this.attribute.value && this.googleMapsInput) {
      this.searchText = this.attribute.value;
      this.googleMapsInput.nativeElement.value = this.attribute.value;
      console.log('Valor inicial carregado:', this.attribute.value);
    }
  }

  private loadGoogleMapsAPI(): void {
    // Verifica se a API já está carregada
    if (this.isGoogleMapsLoaded()) {
      console.log('Google Maps API já estava carregada');
      this.initializeServicesWithRetry();
      return;
    }

    // Verifica se já há um script sendo carregado
    const existingScript = document.querySelector('script[src*="maps.googleapis.com"]');
    if (existingScript) {
      console.log('Google Maps API já está sendo carregada');
      this.waitForApiLoad();
      return;
    }

    console.log('Carregando Google Maps API...');
    const script = this.renderer.createElement('script');
    script.src = `https://maps.googleapis.com/maps/api/js?key=${environment.googleMapsApiKey}&libraries=places&language=pt-BR&region=BR&callback=__googleMapsCallback`;
    script.async = true;
    script.defer = true;
    
    // Cria callback global
    (window as any).__googleMapsCallback = () => {
      console.log('Google Maps API carregada via callback');
      this.isApiLoaded = true;
      this.initializeServicesWithRetry();
    };

    script.onerror = (error) => {
      console.error('Erro ao carregar Google Maps API:', error);
    };

    this.renderer.appendChild(document.head, script);
  }

  private isGoogleMapsLoaded(): boolean {
    return typeof google !== 'undefined' && 
           google.maps && 
           google.maps.places && 
           google.maps.places.AutocompleteService &&
           google.maps.places.PlacesService;
  }

  private waitForApiLoad(): void {
    // Verifica periodicamente se a API foi carregada
    const checkInterval = setInterval(() => {
      if (this.isGoogleMapsLoaded()) {
        clearInterval(checkInterval);
        this.initializeServicesWithRetry();
      }
    }, 100);

    // Timeout após 10 segundos
    setTimeout(() => {
      clearInterval(checkInterval);
      if (!this.isGoogleMapsLoaded()) {
        console.error('Timeout: Google Maps API não carregou em 10 segundos');
      }
    }, 10000);
  }

  private initializeServicesWithRetry(): void {
    if (this.isServicesInitialized) {
      return;
    }

    if (!this.isGoogleMapsLoaded()) {
      this.initRetryCount++;
      if (this.initRetryCount <= this.maxRetries) {
        console.log(`Tentativa ${this.initRetryCount}/${this.maxRetries} - Aguardando Google Maps API...`);
        setTimeout(() => this.initializeServicesWithRetry(), 200 * this.initRetryCount);
      } else {
        console.error('Falha ao inicializar Google Maps após múltiplas tentativas');
      }
      return;
    }

    try {
      this.ngZone.run(() => {
        this.autocompleteService = new google.maps.places.AutocompleteService();
        this.placesService = new google.maps.places.PlacesService(document.createElement('div'));
        this.isServicesInitialized = true;
        console.log('Serviços do Google Maps inicializados com sucesso');
      });
    } catch (error) {
      console.error('Erro ao inicializar serviços Google Maps:', error);
      this.initRetryCount++;
      if (this.initRetryCount <= this.maxRetries) {
        setTimeout(() => this.initializeServicesWithRetry(), 500);
      }
    }
  }

  onInputChange(event: any): void {
    const inputValue = event.target.value;
    this.searchText = inputValue;
    
    if (inputValue.length < 3) {
      this.predictions = [];
      this.showPredictions = false;
      return;
    }

    // Verifica se os serviços estão disponíveis antes de buscar
    if (!this.isServicesInitialized || !this.autocompleteService) {
      console.warn('Serviços Google Maps não estão prontos para busca');
      return;
    }

    this.isLoading = true;
    this.searchPlaces(inputValue);
  }

  private searchPlaces(query: string): void {
    if (!this.autocompleteService) {
      console.warn('AutocompleteService não está disponível');
      this.isLoading = false;
      return;
    }

    const request = {
      input: query,
      language: 'pt-BR'
    };

    try {
      this.autocompleteService.getPlacePredictions(request, (predictions: any, status: any) => {
        this.ngZone.run(() => {
          this.isLoading = false;
          
          if (status === google.maps.places.PlacesServiceStatus.OK && predictions) {
            this.predictions = predictions;
            this.showPredictions = true;
            console.log('Predições encontradas:', predictions.length);
          } else if (status === google.maps.places.PlacesServiceStatus.ZERO_RESULTS) {
            this.predictions = [];
            this.showPredictions = false;
            console.log('Nenhuma predição encontrada para:', query);
          } else {
            this.predictions = [];
            this.showPredictions = false;
            console.warn('Erro na busca de predições:', status);
          }
        });
      });
    } catch (error) {
      this.ngZone.run(() => {
        this.isLoading = false;
        this.predictions = [];
        this.showPredictions = false;
      });
      console.error('Erro ao buscar predições:', error);
    }
  }

  selectPlace(prediction: any): void {
    if (!this.placesService) {
      console.warn('PlacesService não está disponível');
      return;
    }

    const request = {
      placeId: prediction.place_id,
      fields: ['place_id', 'formatted_address', 'name', 'geometry', 'types', 'address_components']
    };

    try {
      this.placesService.getDetails(request, (place: any, status: any) => {
        this.ngZone.run(() => {
          if (status === google.maps.places.PlacesServiceStatus.OK && place) {
            this.setSelectedPlace(place);
            this.searchText = place.formatted_address;
            this.showPredictions = false;
            
            // Atualizar o valor do input
            if (this.googleMapsInput) {
              this.googleMapsInput.nativeElement.value = place.formatted_address;
            }
            console.log('Local selecionado com sucesso');
          } else {
            console.warn('Erro ao obter detalhes do local:', status);
          }
        });
      });
    } catch (error) {
      console.error('Erro ao selecionar local:', error);
    }
  }

  private setSelectedPlace(place: any): void {
    try {
      const placeData = {
        place_id: place.place_id,
        formatted_address: place.formatted_address,
        name: place.name || '',
        latitude: place.geometry?.location?.lat() || null,
        longitude: place.geometry?.location?.lng() || null,
        types: place.types || [],
        address_components: place.address_components || []
      };

      // Armazenar no attribute.attributes
      this.storeLocationData(placeData);
      
      // Atualizar o valor principal
      this.value = place.formatted_address;

      this.executeChangingEvents();
      
      console.log('Dados do local armazenados:', placeData);
    } catch (error) {
      console.error('Erro ao processar dados do local selecionado:', error);
    }
  }

  private storeLocationData(placeData: any): void {
    try {
      // Garante que a lista de atributos exista
      if (!this.attribute.attributes) {
        this.attribute.attributes = [];
      }

      // Encontrar ou criar attribute para armazenar dados de localização
      let locationAttribute = this.findLocationAttribute();
      
      if (!locationAttribute) {
        locationAttribute = {
          key: 'google_maps_data',
          value: JSON.stringify(placeData),
          type: 'Text',
          options: [],
          attributes: []
        } as any;
        this.attribute.attributes.push(locationAttribute);
      } else {
        // Sanitiza o atributo existente para evitar undefined em .options/.attributes
        if (!(locationAttribute as any).options) {
          (locationAttribute as any).options = [];
        }
        if (!(locationAttribute as any).attributes) {
          (locationAttribute as any).attributes = [];
        }
        locationAttribute.value = JSON.stringify(placeData);
      }
    } catch (error) {
      console.error('Erro ao armazenar dados de localização:', error);
    }
  }

  private executeChangingEvents() {
    if (this.valueChanged) {
      let events: DynamicEvent[] = [];

      if (this.attribute.hasError && this.value) {
        this.attribute.hasError = false;
        events.push({ name: "validate" });
      }

      this.shareData(events);

      events.push({ name: "autosave", data: "silently" });
      this.emitEvents(events);
      this.initialValue = this.value;
    }
  }

  private shareData(events?: DynamicEvent[]) {
    let shareData = this.attribute.options
      .some(opt => opt.key.toLowerCase() == "sharedata" &&
        opt.value.toLowerCase() == "true");

    if (shareData && this.attribute.value) {
      if(!this.attribute.uid) {
        this.attribute.uid = performance.now().toString()
      }

      let sharingType = this.attribute.options
        .find(opt => opt.key.toLowerCase() == "sharingtype" && opt.value);

      if (sharingType && sharingType.value.toLowerCase() == "self") {
        let sharedDataKeyOpt = this.attribute.options
          .find(opt => opt.key.toLowerCase() == "SharedDataKey".toLowerCase() && opt.value);

        this.sharedDataService.setItem(
          sharedDataKeyOpt.value,
          { 
            uid: this.attribute.uid, 
            key: this.attribute.value, 
            value: this.attribute.value 
          },
          "items"
        );
      }

      if(events) {
        events.push({ name: "shareData", attribute: this.attribute })
      } else {
        this.emit({ name: "shareData", attribute: this.attribute })
      }
    }
  }

  private findLocationAttribute(): any {
    try {
      if (!this.attribute.attributes) {
        this.attribute.attributes = [];
      }
      const attr = this.attribute.attributes.find((attr: any) => attr && attr.key === 'google_maps_data');
      // Sanitiza caso já exista mas sem estruturas obrigatórias
      if (attr) {
        if (!attr.options) { attr.options = []; }
        if (!attr.attributes) { attr.attributes = []; }
      }
      return attr || null;
    } catch (error) {
      console.error('Erro ao buscar atributo de localização:', error);
      return null;
    }
  }

  clearSelection(): void {
    this.value = '';
    this.searchText = '';
    this.predictions = [];
    this.showPredictions = false;
    
    if (this.googleMapsInput) {
      this.googleMapsInput.nativeElement.value = '';
    }

    // Limpar dados armazenados apenas do atributo de localização, sem apagar outros
    const locationAttribute = this.findLocationAttribute();
    if (locationAttribute) {
      locationAttribute.value = '';
    }
  }

  getValueForView(): string {
    return this.value || 'Nenhum endereço selecionado';
  }

  onBlur(): void {
    // Delay para permitir clique nas sugestões
    setTimeout(() => {
      this.showPredictions = false;
    }, 200);
  }

  onFocus(): void {
    if (this.predictions.length > 0 && this.isServicesInitialized) {
      this.showPredictions = true;
    }
  }

  trackByPlaceId(index: number, prediction: any): string {
    return prediction.place_id;
  }

  getBooleanOptionByKey(attribute: any, key: string): boolean {
    const optionValue = this.getOptionByKey(attribute, key, 'false');
    return optionValue.toLowerCase() === 'true';
  }

  getPlaceholder(attribute: any): string {
    return this.getOptionByKey(attribute, 'placeholder', 'Digite um endereço ou local...');
  }

  get value(): string {
    return this.attribute.value || '';
  }

  set value(input: string) {
    this.attribute.value = input;
  }
}